import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatHorizontalStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import {
    filter,
    map,
    shareReplay,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { IAclID } from 'src/app/acl-data/aclID.interface';
import {
    Document,
    INITIAL_DOCUMENT,
} from 'src/app/acl-data/document/document.dto';
import { DocumentService } from 'src/app/acl-data/document/document.service';
import { Folder } from 'src/app/acl-data/folder/folder.dto';
import { FolderService } from 'src/app/acl-data/folder/folder.service';
import { deepCopy } from 'src/app/acl-data/general/generic.function';
import { IBookmarkEntry } from 'src/app/navigation/menuInterfaces/bookmark-entry.interface';
import { TopnavInfoService } from 'src/app/navigation/menuInterfaces/topnav-info.service';
import { DocumentPropertiesEditComponent } from 'src/app/shared-controls/document-properties-edit/document-properties-edit.component';
import { SubSink } from 'subsink';

import { DocumentUploadSelectComponent } from '../document-upload-select/document-upload-select.component';

export enum SELECTED_TAB {
    Upload = 0,
    Properties = 1,
}

@Component({
    templateUrl: './document-add.component.html',
    styleUrls: ['./document-add.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentAddComponent implements OnDestroy, AfterViewChecked {
    subSink = new SubSink();

    @ViewChild('properties') properties: DocumentPropertiesEditComponent;
    @ViewChild('upload') upload: DocumentUploadSelectComponent;

    ADDENDUM = 22;

    // public file: IFileData;
    public document: Document = deepCopy(INITIAL_DOCUMENT);
    public SELECTED_TAB = SELECTED_TAB;

    stopDuplicateCheck = false;
    busyUploading = false;
    uploadFailed = false;
    duplicates$: Observable<Document[]>;
    docTitle$: BehaviorSubject<string> = new BehaviorSubject('');

    folderID$ = this.route.paramMap.pipe(
        map(v => v.get('folderID')),
        tap(v => (this.document.propertiesObj.FoldersFiledInID = v)), // Set the folder ID for the document
        withLatestFrom(this.route.data),

        map(([idValue, routeValue]) => {
            return {
                id: idValue,
                objectStore: routeValue.parentMenu.data.id,
            } as IAclID;
        })
    );

    folder$ = this.folderID$.pipe(
        switchMap(v => this.folderService.getByID(v.objectStore, v.id))
    ) as Observable<Folder>;

    clear = () => {
        // this.stopDuplicateCheck = false;
        this.busyUploading = false;
        this.uploadFailed = false;
        this.document = deepCopy(INITIAL_DOCUMENT);
        this.changed(null);
    };

    changed = event => {
        if (this.document.propertiesObj.DocumentTitle) {
            this.docTitle$.next(this.document.propertiesObj.DocumentTitle);
        }
    };

    ngAfterViewChecked(): void {
        if (this.upload && this.docTitle$ && !this.duplicates$) {
            this.duplicates$ = combineLatest([
                this.upload.file,
                this.docTitle$,
                this.folderID$,
                this.folder$,
            ]).pipe(
                filter(_ => !this.busyUploading && !this.stopDuplicateCheck),
                filter(([file, title, id]) => !!file && !!id),
                // tap(([file, title, id, folder]) =>
                //     console.log(
                //         `/${folder.propertiesObj.PathName.split('/')[1]}`
                //     )
                // ),
                switchMap(([file, title, id, folder]) =>
                    this.documentService
                        .getDuplicateDocuments(
                            id.objectStore,
                            title,
                            file.hash,
                            `${file.size}`,
                            `/${folder.propertiesObj.PathName.split('/')[1]}`
                        )
                        .pipe(
                            tap(
                                success => {},
                                e =>
                                    this._snackBar.open(
                                        'Unable to check for duplicates',
                                        'Dismiss'
                                    )
                            )
                        )
                ),
                shareReplay()
            );
        }
    }

    ngOnDestroy(): void {
        this.subSink.unsubscribe();
    }

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private folderService: FolderService,
        private documentService: DocumentService,
        private topNavInfoService: TopnavInfoService,
        private cdr: ChangeDetectorRef,
        private _snackBar: MatSnackBar
    ) {
        this.subSink.add(
            this.folder$.subscribe(v => {
                if (!!v.propertiesObj.FolderFilterClasses) {
                    this.document.propertiesObj.ClassDescription =
                        v.propertiesObj.FolderFilterClasses[0];
                }

                if (
                    this.document.propertiesObj.ClassDescription ===
                    'TransactionContract'
                ) {
                    this.document.propertiesObj.ContractNoAlertRequired = true;
                }
            })
        );

        this.subSink.add(
            this.documentService.failed$.subscribe(error => {
                this.busyUploading = false;
                this.uploadFailed = true;
                cdr.detectChanges();
            })
        );

        this.subSink.add(
            combineLatest([
                this.route.paramMap,
                this.route.data,
                this.folder$,
                this.folderService.entities$,
            ])
                .pipe(
                    tap(([parmMap, routeValue, currentFolder, entities]) => {
                        const objectStore = routeValue.parentMenu.data.id;

                        if (!currentFolder) {
                            return;
                        }
                        const result: IBookmarkEntry[] = [];

                        if (currentFolder.parentID) {
                            const currentFolderParent = entities.find(
                                v => v.id === currentFolder.parentID
                            );

                            if (!currentFolderParent) {
                                this.folderService
                                    .getByID(
                                        objectStore,
                                        currentFolder.parentID
                                    )
                                    .pipe(take(1))
                                    .subscribe(_ => {
                                        return;
                                    });
                            }
                            if (
                                currentFolderParent &&
                                !currentFolderParent.isRoot
                            ) {
                                result.push({
                                    path: `/${objectStore}/browse/${currentFolderParent.id}`,
                                    description:
                                        currentFolderParent.propertiesObj
                                            .FolderName,
                                });
                            }
                        }

                        result.push({
                            path: `/${objectStore}/browse/${currentFolder.id}`,
                            description: currentFolder.propertiesObj.FolderName,
                        });

                        result.push({
                            path: `/${objectStore}/DocumentAdd/${currentFolder.id}`,
                            description: 'Add New Document',
                        });

                        this.topNavInfoService.addExtraBreadCrumbData(result);
                    })
                )
                .subscribe(v => {
                    return;
                })
        );
    }

    step = (input: MatHorizontalStepper) => {
        if (input.selectedIndex === SELECTED_TAB.Properties) {
            this.properties.touchAll();

            if (!this.properties.isValid) {
                this.properties.focusOnError();
            }
        }
    };

    linkedDocumentRequired = () => {
        return (
            this.document &&
            this.document.propertiesObj.ContractAgreementType === this.ADDENDUM
        );
    };

    save() {
        console.log('Saving', this.document);
        
        delete this.document.propertiesObj.ContractLegalCounselEmail
        this.busyUploading = true;
        this.stopDuplicateCheck = true;
        combineLatest([this.upload.file, this.folderID$, this.folder$])
            .pipe(take(1))
            .subscribe(([file, folderID]) => {
                this.document.file = file;
                this.document.propertiesObj.ObjectStore = folderID.objectStore;
                this.document.propertiesObj.FoldersFiledInID = folderID.id;
                this.document.propertiesObj.ContractFileHash = file.hash;
                this.documentService
                    .add(this.document, {})
                    .pipe(take(1))
                    .subscribe(newDoc => {
                        console.log('Trying to navigate to ', newDoc.id);
                        this.folderService.updateFolderInCache(
                            folderID.id,
                            newDoc.id
                        );
                        // .getByID(folderID.objectStore, folderID.id, true)
                        // .pipe(take(1))
                        // .subscribe(_ => {
                        //     return;
                        // }); //Fetch folder again to update change

                        this.clear();
                        // console.log('Navigating: Add Component');
                        this.router.navigate(
                            [
                                '/',
                                folderID.objectStore,
                                'DocumentView',
                                newDoc.id,
                            ],
                            {
                                relativeTo: this.route,
                                queryParamsHandling: 'merge',
                            }
                        );
                    });
            });
    }
}
